// Services Map: danh sách tuyến (POL → POD) với tình trạng giá + danh sách service theo nhóm tuyến.
import type { RegionGroup } from './costRates';
import { REGION_GROUPS, groupOfPod, routeLabel } from './costRates';
import type { CellStatus, Grid } from './costRates';
import type { CostRate, Service } from './types';

export const PICS: readonly string[] = ['TH', 'LN', 'PV', 'MA'];

export interface LaneRow {
  key: string;
  pol: string;
  pod: string;
  label: string;
  group: RegionGroup;
  /** hãng có giá còn hạn / hãng đã khai tuyến */
  live: number;
  total: number;
  /** T/T ngắn nhất (ngày) và có chuyển tải không */
  tt: number | null;
  viaOnly: boolean;
  /** chuyến mỗi tuần (tổng các service ghé cả POL và POD theo thứ tự) */
  freq: number;
  /** tình trạng giá chung của tuyến (xem `laneRows`) */
  status: CellStatus;
  /** hỏi giá chờ */
  pending: number;
  pic: string;
  follow: boolean;
}

const hash = (s: string) => {
  let h = 2166136261;
  for (const c of s) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
  return h >>> 0;
};

/** Service ghé POL rồi POD (theo thứ tự rotation). */
export const servesLane = (s: Service, pol: string, pod: string): boolean => {
  const a = s.rotation.indexOf(pol);
  const b = s.rotation.indexOf(pod);
  return a >= 0 && b > a;
};

export function laneRows(grid: Grid, rates: readonly CostRate[], services: readonly Service[]): LaneRow[] {
  const tt = new Map<string, { min: number; direct: boolean }>();
  for (const r of rates) {
    const k = `${r.pol}→${r.pod}`;
    const cur = tt.get(k);
    const direct = r.via === null;
    if (!cur || r.transitDays < cur.min) tt.set(k, { min: r.transitDays, direct: direct || (cur?.direct ?? false) });
    else if (direct && !cur.direct) cur.direct = true;
  }
  return grid.rows.map((row) => {
    const [pol = '', pod = ''] = row.route.split(' → ');
    const key = `${pol}→${pod}`;
    // không hãng nào có giá còn hạn → hết hạn / chưa có; mọi giá còn hạn đều sắp hết (≤ 7 ngày) → sắp hết; còn lại → còn hạn
    const cells = row.cells.filter((c): c is NonNullable<typeof c> => !!c);
    const status: CellStatus = row.covered === 0 ? (cells.some((c) => c.status === 'exp') ? 'exp' : 'none') : cells.every((c) => c.status !== 'ok') ? 'soon' : 'ok';
    const t = tt.get(key);
    const h = hash(key);
    return {
      key,
      pol,
      pod,
      label: routeLabel({ pol, pod }),
      group: row.group,
      live: row.covered,
      total: row.served,
      tt: t?.min ?? null,
      viaOnly: !!t && !t.direct,
      freq: services.filter((s) => servesLane(s, pol, pod)).reduce((n, s) => n + s.freqPerWeek, 0),
      status,
      pending: h % 7 === 0 ? 1 + (h % 3) : 0,
      pic: PICS[h % PICS.length] as string,
      follow: h % 3 === 0,
    };
  });
}

export function groupBy<T>(rows: readonly T[], groupOf: (r: T) => RegionGroup): { key: RegionGroup; label: string; rows: T[] }[] {
  return REGION_GROUPS.map((g) => ({ ...g, rows: rows.filter((r) => groupOf(r) === g.key) })).filter((g) => g.rows.length > 0);
}

/** Nhóm của service = nhóm vùng của cảng đến đầu tiên không phải VN. */
export const serviceGroup = (s: Service): RegionGroup => groupOfPod(s.rotation[1] ?? s.rotation[0] ?? '');

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd').toLowerCase();

export const matchLane = (l: LaneRow, q: string): boolean => !q.trim() || fold(`${l.pol} ${l.pod} ${l.group}`).includes(fold(q.trim()));
export const matchService = (s: Service, q: string): boolean => !q.trim() || fold(`${s.id} ${s.name} ${s.carriers.join(' ')} ${s.rotation.join(' ')}`).includes(fold(q.trim()));
